import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Users } from "lucide-react";
import { api } from "../lib/api";
import { formatINR } from "../lib/format";

// Toggle-able referral-tracking section for the agent/broker Earnings pages.
// Shows how many people signed up via the user's referral link, how many
// onboarded, how many completed their first agreement (and how many of those
// were deposit-protected), and the referral commission actually earned — read
// straight from the same wallet-transaction log that funds the wallet balance.
export default function ReferralTrackingPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !data) {
      setLoading(true);
      api.get("/referral")
        .then(({ data }) => setData(data))
        .catch((e) => console.error("Failed to load referral tracking:", e))
        .finally(() => setLoading(false));
    }
  }, [open, data]);

  return (
    <div className="card-base overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[#F97316]" />
          <span className="font-semibold text-gray-900">Referral Earnings Tracking</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
          {loading && <div className="text-sm text-gray-500">Loading…</div>}

          {!loading && data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold text-[#F97316]">{data.referrals_total || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Referrals signed up</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold text-[#F97316]">{data.referrals_onboarded || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Onboarded (KYC done)</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold text-[#F97316]">{data.referrals_completed_first_agreement || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Completed 1st agreement</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold text-emerald-600">{formatINR(data.referral_commission_earned_paise)}</div>
                  <div className="text-xs text-gray-500 mt-1">Referral commission earned</div>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Of the {data.referrals_completed_first_agreement || 0} who completed a first agreement,{" "}
                <span className="font-medium text-gray-700">{data.referrals_completed_deposit_protected || 0}</span>{" "}
                had their deposit held with Orello.
              </p>

              <div className="bg-[#FFF4ED] border border-indigo-100 rounded-xl p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Agents/brokers referred who've closed a paid deal</span>
                  <span className="font-semibold text-[#F97316]">
                    {data.qualified_referred_brokers_count || 0} / 5
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {data.broker_recruit_bonus_paid
                    ? "₹250 network-growth bonus already earned"
                    : "Reach 5 to earn a one-time ₹250 bonus"}
                </p>
              </div>

              <p className="text-[11px] text-gray-400">
                Referral commission earned is pulled directly from your wallet's transaction
                log — the same figure that funds your wallet balance.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
